import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { TRANSLATIONS_ENABLED, TRANSLATIONS_LOCALE } from './constants';

const I18nContext = createContext(null);

function interpolate(template, vars) {
	if (!vars) return String(template);

	return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => String(vars[k] ?? ''));
}

const DICTS = {
	default: {
		en: require('locales/default/en/common.json'),
		fr: require('locales/default/fr/common.json'),
		pir: require('locales/default/pir/common.json'),
		wl: require('locales/default/wl/common.json'),
		na: require('locales/default/na/common.json'),
	},
	usw: {
		en: require('locales/usw/en/common.json'),
		wl: require('locales/usw/wl/common.json'),
	},
	hylton: {
		en: require('locales/hylton/en/common.json'),
		pir: require('locales/hylton/pir/common.json'),
		na: require('locales/hylton/na/common.json'),
	},
};

function isValidType(t) {
	return typeof t === 'string' && !!DICTS[t];
}

function getSafeType(type) {
	return isValidType(type) ? type : 'default';
}

function isValidLocale(locale, type) {
	if (typeof locale !== 'string') return false;

	const safeType = getSafeType(type);
	return Object.keys(DICTS[safeType] || {}).includes(locale);
}

function getSafeLocale(locale, type) {
	const safeType = getSafeType(type);
	const available = Object.keys(DICTS[safeType] || {});

	if (isValidLocale(locale, safeType)) {
		return locale;
	}

	if (available.includes('en')) {
		return 'en';
	}

	return available[0] || 'en';
}

export function I18nProvider({ children, initialLocale = 'en', initialType = TRANSLATIONS_LOCALE }) {
	const safeInitialType = getSafeType(initialType);
	const safeInitialLocale = getSafeLocale(initialLocale, safeInitialType);

	const [locale, setLocaleState] = useState(safeInitialLocale);
	const [langType, setLangTypeState] = useState(safeInitialType);
	const [translations_ready, setTranslationsReady] = useState(false);

	useEffect(() => {
		try {
			const savedLocale = window.localStorage.getItem('locale');

			if (isValidLocale(savedLocale, safeInitialType)) {
				setLocaleState(savedLocale);
			}

			// Important: do not read langType from localStorage.
			// The language group should come from NEXT_PUBLIC_LANGUAGE_GROUP.
			window.localStorage.setItem('langType', safeInitialType);
		} catch (e) {}

		setTranslationsReady(true);
	}, [safeInitialType]);

	const setLocale = (nextLocale) => {
		if (!isValidLocale(nextLocale, langType)) return;

		setLocaleState(nextLocale);

		if (typeof window !== 'undefined') {
			try {
				window.localStorage.setItem('locale', nextLocale);
			} catch (e) {}
		}
	};

	const setLangType = (nextType) => {
		if (!isValidType(nextType)) return;

		const nextLocale = getSafeLocale(locale, nextType);

		setLangTypeState(nextType);
		setLocaleState(nextLocale);

		if (typeof window !== 'undefined') {
			try {
				window.localStorage.setItem('langType', nextType);
				window.localStorage.setItem('locale', nextLocale);
			} catch (e) {}
		}
	};

	const value = useMemo(() => {
		const dict = DICTS[langType]?.[locale] || DICTS.default?.[locale] || DICTS.default?.en || {};

		const t = (key, fallback, vars) => {
			if (!TRANSLATIONS_ENABLED) {
				return interpolate(fallback ?? key, vars);
			}

			const template = dict[key] ?? fallback ?? key;
			return interpolate(template, vars);
		};

		return {
			locale,
			setLocale,
			langType,
			setLangType,
			t,
			translations_ready,
		};
	}, [locale, langType, translations_ready]);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
	const ctx = useContext(I18nContext);

	if (!ctx) {
		throw new Error('useI18n must be used within I18nProvider');
	}

	return ctx;
}

export function useT() {
	return useI18n().t;
}
